import React from 'react';
import {
  Box,
  Card,
  Typography,
  Chip,
  alpha,
  useTheme
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

/**
 * SimpleForecastCard Component - Clean Text-Focused Layout
 * 
 * No internal borders or backgrounds
 * Focus on typography and clear text hierarchy
 */
const SimpleForecastCard = ({ target, selectedHorizon = '3M' }) => {
  const { i18n, t } = useTranslation();
  const theme = useTheme();
  const currentLang = i18n.language;

  // Get current value
  const currentValue = target.latest_actual?.value;
  const currentDate = target.latest_actual?.date;

  // Get forecasts
  const forecast1M = target.forecasts?.['1M']?.BASE;
  const forecast3M = target.forecasts?.['3M']?.BASE;
  const forecast6M = target.forecasts?.['6M']?.BASE;
  const forecast1Y = target.forecasts?.['1Y']?.BASE;

  // Get selected forecast based on selectedHorizon
  const getSelectedForecast = () => {
    switch (selectedHorizon) {
      case '1M': return forecast1M;
      case '3M': return forecast3M;
      case '6M': return forecast6M;
      case '1Y': return forecast1Y;
      default: return forecast3M;
    }
  };

  const selectedForecast = getSelectedForecast();

  // Calculate trend based on selected horizon
  const getTrend = () => {
    if (!currentValue || !selectedForecast?.mean) return 'flat';
    const change = ((selectedForecast.mean - currentValue) / currentValue) * 100;
    if (change > 5) return 'up';
    if (change < -5) return 'down';
    return 'flat';
  };

  const trend = getTrend();

  // Get trend icon and color
  const getTrendDisplay = () => {
    switch (trend) {
      case 'up':
        return {
          icon: <TrendingUpIcon sx={{ fontSize: 14 }} />,
          color: theme.palette.success.main,
          label: t('forecast.trends.up', 'Up'),
          bgcolor: alpha(theme.palette.success.main, 0.12)
        };
      case 'down':
        return {
          icon: <TrendingDownIcon sx={{ fontSize: 14 }} />,
          color: theme.palette.error.main,
          label: t('forecast.trends.down', 'Down'),
          bgcolor: alpha(theme.palette.error.main, 0.12)
        };
      default:
        return {
          icon: <TrendingFlatIcon sx={{ fontSize: 14 }} />,
          color: theme.palette.warning.main,
          label: t('forecast.trends.stable', 'Stable'),
          bgcolor: alpha(theme.palette.warning.main, 0.12)
        };
    }
  };

  const trendDisplay = getTrendDisplay();

  // Format value
  const formatValue = (value) => {
    if (value == null) return 'N/A';
    return `${value.toFixed(2)}%`;
  };

  // Get horizon label
  const getHorizonLabel = (horizon) => {
    const labels = {
      '1M': t('forecast.horizons.1m', '1M'),
      '3M': t('forecast.horizons.3m', '3M'),
      '6M': t('forecast.horizons.6m', '6M'),
      '1Y': t('forecast.horizons.1y', '1Y')
    };
    return labels[horizon] || horizon;
  };

  // Get interpretation
  const getInterpretation = () => {
    if (!currentValue || !selectedForecast?.mean) {
      return {
        vi: t('forecast.interpretation.waitingData', 'Waiting for data...'),
        en: 'Waiting for data...'
      };
    }

    const change = selectedForecast.mean - currentValue;
    const changePercent = ((change / currentValue) * 100).toFixed(1);
    const horizonLabel = getHorizonLabel(selectedHorizon);

    if (trend === 'up') {
      return {
        vi: t('forecast.interpretation.expectedUp', 'Expected +{{pct}}% in {{horizon}}. {{note}}', { pct: Math.abs(changePercent), horizon: horizonLabel, note: target.target_id === 'CPI_YOY' ? t('forecast.interpretation.inflationMayRise', 'Inflation may rise.') : t('forecast.interpretation.positiveSignal', 'Positive signal.') }),
        en: `Expected +${Math.abs(changePercent)}% in ${horizonLabel}. ${target.target_id === 'CPI_YOY' ? 'Inflation may rise.' : 'Positive signal.'}`
      };
    } else if (trend === 'down') {
      return {
        vi: t('forecast.interpretation.expectedDown', 'Expected -{{pct}}% in {{horizon}}. {{note}}', { pct: Math.abs(changePercent), horizon: horizonLabel, note: target.target_id === 'CPI_YOY' ? t('forecast.interpretation.inflationMayFall', 'Inflation may fall.') : t('forecast.interpretation.monitorClosely', 'Monitor closely.') }),
        en: `Expected -${Math.abs(changePercent)}% in ${horizonLabel}. ${target.target_id === 'CPI_YOY' ? 'Inflation may fall.' : 'Monitor closely.'}`
      };
    } else {
      return {
        vi: t('forecast.interpretation.expectedStable', 'Expected stable in {{horizon}}.', { horizon: horizonLabel }),
        en: `Expected stable in ${horizonLabel}.`
      };
    }
  };

  const interpretation = getInterpretation();

  // Calculate change for selected forecast
  const changeValue = currentValue && selectedForecast?.mean 
    ? selectedForecast.mean - currentValue 
    : null;
  const changePercent = currentValue && changeValue !== null
    ? ((changeValue / currentValue) * 100).toFixed(1)
    : null;

  return (
    <Card 
      elevation={0}
      sx={{ 
        mb: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        overflow: 'hidden',
        transition: 'all 0.2s ease',
        backgroundColor: 'background.paper',
        '&:hover': {
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          borderColor: alpha(trendDisplay.color, 0.3)
        }
      }}
    >
      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: { 
          xs: '1fr', 
          sm: '200px 140px 200px 1fr 240px',
          lg: '220px 160px 200px 1fr 260px'
        },
        gap: 0,
        alignItems: 'stretch'
      }}>
        {/* Column 1: Title */}
        <Box sx={{ 
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}>
          <Typography 
            variant="subtitle2" 
            sx={{ 
              fontSize: '0.875rem',
              fontWeight: 600,
              color: 'text.primary',
              lineHeight: 1.4,
              mb: 0.5
            }}
          >
            {target.target_name?.[currentLang] || target.target_name?.vi || target.target_name?.en || 'N/A'}
          </Typography>
          {currentDate && (
            <Typography 
              variant="caption" 
              sx={{ 
                fontSize: '0.75rem',
                color: 'text.secondary'
              }}
            >
              {new Date(currentDate).toLocaleDateString(currentLang === 'vi' ? 'vi-VN' : 'en-US', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
              })}
            </Typography>
          )}
        </Box>

        {/* Column 2: Current Value */}
        <Box sx={{ 
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 0.75
        }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 700,
                fontSize: '1.5rem',
                lineHeight: 1,
                color: trendDisplay.color
              }}
            >
              {formatValue(currentValue)}
            </Typography>
            <Chip
              icon={trendDisplay.icon}
              label={trendDisplay.label}
              size="small"
              sx={{
                bgcolor: trendDisplay.bgcolor,
                color: trendDisplay.color,
                fontWeight: 500,
                fontSize: '0.7rem',
                height: 22,
                '& .MuiChip-icon': {
                  color: trendDisplay.color,
                  fontSize: 12
                }
              }}
            />
          </Box>
        </Box>

        {/* Column 3: Selected Forecast */}
        {selectedForecast && (
          <Box sx={{ 
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 0.5
          }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 700,
                  fontSize: '1.4rem',
                  color: 'primary.main',
                  lineHeight: 1
                }}
              >
                {formatValue(selectedForecast.mean)}
              </Typography>
              {changePercent && (
                <Chip
                  label={`${changeValue > 0 ? '+' : ''}${changePercent}%`}
                  size="small"
                  sx={{
                    bgcolor: changeValue > 0 ? alpha(theme.palette.success.main, 0.15) : changeValue < 0 ? alpha(theme.palette.error.main, 0.15) : alpha(theme.palette.grey[500], 0.15),
                    color: changeValue > 0 ? theme.palette.success.dark : changeValue < 0 ? theme.palette.error.dark : 'text.secondary',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    height: 20
                  }}
                />
              )}
            </Box>
            {selectedForecast.lower && selectedForecast.upper && (
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  lineHeight: 1.3
                }}
              >
                {t('forecast.range', 'Range')}: {formatValue(selectedForecast.lower)} - {formatValue(selectedForecast.upper)}
              </Typography>
            )}
          </Box>
        )}

        {/* Column 4: Other Horizons */}
        <Box sx={{ 
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap'
        }}>
          {[
            { key: '1M', forecast: forecast1M },
            { key: '3M', forecast: forecast3M },
            { key: '6M', forecast: forecast6M },
            { key: '1Y', forecast: forecast1Y }
          ].filter(item => item.key !== selectedHorizon).map(({ key, forecast }) => (
            <Box
              key={key}
              sx={{ 
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 0.25
              }}
            >
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5
                }}
              >
                {key}
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontWeight: 600,
                  color: 'text.primary',
                  fontSize: '0.9375rem',
                  lineHeight: 1.2
                }}
              >
                {formatValue(forecast?.mean)}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Column 5: Meaning */}
        <Box sx={{ 
          p: 2,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 0.75
        }}>
          <Box sx={{ 
            color: 'primary.main', 
            display: 'flex', 
            alignItems: 'center',
            mt: 0.25,
            flexShrink: 0
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18h6"/><path d="M10 22h4"/><path d="M12 2a7 7 0 0 1 7 7c0 2.4-1.2 4.5-3 5.7V16a1 1 0 0 1-1 1h-6a1 1 0 0 1-1-1v-1.3C6.2 13.5 5 11.4 5 9a7 7 0 0 1 7-7z"/>
            </svg>
          </Box>
          <Typography 
            variant="body2" 
            sx={{ 
              lineHeight: 1.5,
              fontSize: '0.8125rem',
              color: 'text.primary',
              fontWeight: 400
            }}
          >
            {interpretation[currentLang]}
          </Typography>
        </Box>
      </Box>
    </Card>
  );
};

export default SimpleForecastCard;
